import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  numberAttribute,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  RendererFactory2,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Brand, CustomSearchResults, SearchHistoryItem, Sts, Tap } from '@search/const';
// Импортировано напрямую осознано, так как сторибук не хочет работать
// eslint-disable-next-line @nx/enforce-module-boundaries
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BlurOnTouchOutsideDirective,
  SdModal,
  SdModalService,
  SdvorIconComponent,
  SdvorLoaderComponent,
} from '@sdvor/shared/ui/v2';
import { TSearchBox } from '@search/const/v2';
import { SearchBarService } from '@search/feature';
import { DeviceService, UserAgentService } from '@shared/services';
import { IconTypes, NavigationSourceService } from '@shared/v2';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Component({
  selector: 'sd-searchbox',
  templateUrl: './searchbox.component.html',
  styleUrls: ['./searchbox.component.scss'],
  imports: [AsyncPipe, ReactiveFormsModule, BlurOnTouchOutsideDirective, SdvorIconComponent, SdvorLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.expanded]': 'isExpanded()',
    '(document:mousedown)': 'onGlobalClick($event)',
  },
})
export class SdvorSearchboxComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() type: TSearchBox;
  @Input() searchInputControl: FormControl;
  @Input({ transform: numberAttribute }) maxLength: number;
  @Input({ transform: booleanAttribute }) isLoading: boolean;
  @Input({ transform: booleanAttribute }) isEmpty: boolean;
  @Input() results: CustomSearchResults;
  @Input() query: string;
  @Input() placeholder = '';

  @Output() isExpandedChange = new EventEmitter<boolean>();
  @Output() clear = new EventEmitter<void>();
  /* eslint-disable @angular-eslint/no-output-on-prefix */
  @Output() onFocusSearchInput = new EventEmitter<void>();
  @Output() onEnterSearchInput = new EventEmitter<void>();
  @Output() searchHistoryReset = new EventEmitter<void>();
  @Output() searchHistoryRemove = new EventEmitter<SearchHistoryItem>();
  @Output() onClickTapItem = new EventEmitter<Tap>();
  @Output() onClickHistoryItem = new EventEmitter<SearchHistoryItem>();
  @Output() onClickStsItem = new EventEmitter<Sts>();
  @Output() onClickCategoryItem = new EventEmitter<{ name: string; code: string; url?: string }>();
  @Output() onClickBrandItem = new EventEmitter<Brand>();
  @Output() isOpenedChange = new EventEmitter<boolean>();
  /* eslint-enable */

  @ViewChild('searchInput', { read: ElementRef }) searchInputRef: ElementRef;
  @ViewChildren('searchResultItem', { read: ElementRef }) searchResultItems: QueryList<ElementRef>;

  iconTypes = IconTypes;
  showNavArrow$: Observable<boolean>;
  showOverlay$: Observable<boolean>;
  isDesktopUp$: Observable<boolean> = this.deviceService.isDesktopLgUp$;
  isOpened = signal<boolean>(false);

  isExpanded = signal<boolean>(false);

  private isIOS = signal(false);
  private renderer: Renderer2;
  private intersectionObserver: IntersectionObserver;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private searchBarService: SearchBarService,
    private hostRef: ElementRef,
    private router: Router,
    private destroyRef: DestroyRef,
    private deviceService: DeviceService,
    private modalService: SdModalService,
    private activatedRoute: ActivatedRoute,
    private userAgentService: UserAgentService,
    private navigationSourceService: NavigationSourceService,
    @Optional() private sdModal: SdModal | null,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnInit(): void {
    this.showNavArrow$ = this.isHomepageOpened().pipe(
      map((isHomepage) => {
        const isModalOpened = this.openedModalWindow();

        return isModalOpened || (this.type === 'modal' && !isHomepage);
      }),
    );

    this.showOverlay$ = combineLatest({
      isDesktopUp: this.isDesktopUp$,
      isHomepageOpened: this.isHomepageOpened(),
    }).pipe(map(({ isDesktopUp, isHomepageOpened }) => isDesktopUp && !isHomepageOpened && this.isExpanded()));

    this.userAgentService
      .isIOS()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((isIos) => this.isIOS.set(isIos));
  }

  ngAfterViewInit(): void {
    this.searchBarService
      .getOpenEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.searchInputRef.nativeElement.focus());

    if (this.openedModalWindow()) {
      setTimeout(() => {
        this.searchInputRef.nativeElement.focus();
        this.open();
      }, 0);
    }

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationStart && !(<NavigationStart>event).url.match(/^\w+\/search/)),
      )
      .subscribe(() => {
        if (!this.isEmpty) {
          this.close();
        }
      });
  }

  ngOnDestroy(): void {
    this.toggleSearchboxBodyClass(false);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Закрывать выпадающий список с результатами поиска
   * при нажатии вне хост контейнера
   */
  onGlobalClick(event: Event): void {
    if (!this.hostRef.nativeElement.contains(event.target) && !this.openedModalWindow()) {
      this.close();
    }
  }

  open(): void {
    this.toggleSearchboxBodyClass(true);
    this.isOpened.set(true);
    this.isOpenedChange.emit(true);
  }

  close(): void {
    this.toggleSearchboxBodyClass(false);
    this.isOpened.set(false);
    this.isOpenedChange.emit(false);
    this.isExpanded.set(false);
    this.isExpandedChange.emit(false);
  }

  closeModal(): void {
    if (this.type === 'modal') {
      this.modalService.closeActiveModal();
    }
  }

  onFocusInputFromModal(): void {
    if (this.openedModalWindow()) {
      this.onFocusSearchInput.emit();
      this.searchInputRef.nativeElement.focus();
    }
  }

  onFocusInput(): void {
    this.onFocusSearchInput.emit();
    this.open();
    this.isExpanded.set(true);
    this.isExpandedChange.emit(true);
  }

  onOverlayClick(event: MouseEvent): void {
    event.stopPropagation();
    this.close();
    this.searchInputRef?.nativeElement?.blur();
  }

  /**
   * Фокус следующего HTML элемента в результатах поиска
   */
  focusNextChild(event: Event): void {
    event.preventDefault();
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    if (results.length) {
      if (focusedIndex >= results.length - 1) {
        results[0].focus();
      } else {
        results[focusedIndex + 1].focus();
      }
    }
  }

  /**
   * Фокус предыдущего HTML элемента в результатах поиска
   */
  focusPreviousChild(event: Event): void {
    event.preventDefault();
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    if (results.length) {
      if (focusedIndex < 1) {
        results[results.length - 1].focus();
      } else {
        results[focusedIndex - 1].focus();
      }
    }
  }

  /**
   * Открыто в модальном окне
   */
  openedModalWindow(): boolean {
    return Boolean(this.sdModal);
  }

  /**
   * Стрелка "назад" для перехода на предыдущую страницу
   */
  onClickNavArrow(): void {
    if (this.openedModalWindow()) {
      this.modalService.closeActiveModal();
      this.clear.emit();
      this.close();
    } else {
      this.navigationSourceService.goBack();
    }
  }

  clickTapItem(event: MouseEvent, item: Tap): void {
    event.preventDefault();

    this.onClickTapItem.emit(item);

    setTimeout(() => {
      const input = this.searchInputRef.nativeElement;
      const length = input.value.length;
      input.setSelectionRange(length, length);

      this.onFocusInputFromModal();
    });
  }

  /**
   * Проверяем находимся ли на главной странице
   */
  isHomepageOpened(): Observable<boolean> {
    return this.activatedRoute.data.pipe(
      map((data) => {
        return data.semanticName === 'homepage';
      }),
    );
  }

  /**
   * Получить сфокусированный HTML элемент из результатов поиска
   */
  private getFocusedIndex(): number {
    return this.getResultElements().indexOf(<HTMLElement>this.document.activeElement);
  }

  /**
   * Получить список всех HTML элементов из результатов поиска
   */
  private getResultElements(): HTMLElement[] {
    return this.searchResultItems.map((ref) => ref.nativeElement);
  }

  private toggleSearchboxBodyClass(isOpened: boolean): void {
    if (isOpened) {
      this.renderer.addClass(this.document.body, 'searchbox-is-active');
    } else {
      this.renderer.removeClass(this.document.body, 'searchbox-is-active');
    }
  }
}
